// i18n Configuration
export type SupportedLanguage = 'en' | 'es' | 'pt-BR' | 'pt-PT';

export interface I18nConfig {
  language: SupportedLanguage;
  currency: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  timezone: string;
  numberFormat: string;
}

export const defaultI18nConfig: I18nConfig = {
  language: 'en',
  currency: 'USD',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
  timezone: 'America/New_York',
  numberFormat: 'en-US',
};

export const languages = {
  en: { name: 'English', nativeName: 'English', flag: '🇺🇸' },
  es: { name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  'pt-BR': { name: 'Portuguese (Brazil)', nativeName: 'Português (Brasil)', flag: '🇧🇷' },
  'pt-PT': { name: 'Portuguese (Portugal)', nativeName: 'Português (Portugal)', flag: '🇵🇹' },
};

const LEGACY_LANGUAGE_MAP: Record<string, SupportedLanguage> = {
  pt: 'pt-BR',
  'pt-br': 'pt-BR',
  'pt-pt': 'pt-PT',
};

export function normalizeLanguage(value: unknown): SupportedLanguage {
  if (typeof value !== 'string') return defaultI18nConfig.language;
  const key = value.trim();
  if (key in languages) return key as SupportedLanguage;
  const mapped = LEGACY_LANGUAGE_MAP[key.toLowerCase()];
  if (mapped) return mapped;
  return defaultI18nConfig.language;
}

export const formatDate = (date: Date, format: string, locale = 'en-US'): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  switch (format) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'DD.MM.YYYY':
      return `${day}.${month}.${year}`;
    default:
      return date.toLocaleDateString(locale);
  }
};

export const formatTime = (date: Date, format: '12h' | '24h', locale = 'en-US'): string => {
  return date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: format === '12h',
  });
};

export const formatCurrency = (amount: number, currency: string, locale: string): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const formatNumber = (number: number, locale: string): string => {
  return new Intl.NumberFormat(locale).format(number);
};

function isValidCurrency(code: string): boolean {
  try {
    new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(0);
    return true;
  } catch {
    return false;
  }
}

function sanitizeI18nConfig(parsed: Partial<I18nConfig>): I18nConfig {
  const language = normalizeLanguage(parsed.language);
  const currency =
    parsed.currency && isValidCurrency(parsed.currency) ? parsed.currency : defaultI18nConfig.currency;
  const timeFormat = parsed.timeFormat === '24h' ? '24h' : '12h';
  return {
    ...defaultI18nConfig,
    ...parsed,
    language,
    currency,
    timeFormat,
  };
}

export const loadI18nConfig = (): I18nConfig => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = localStorage.getItem('i18n_config');
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<I18nConfig>;
        return sanitizeI18nConfig(parsed);
      }
    }
  } catch (error) {
    console.error('Error loading i18n config:', error);
  }
  return defaultI18nConfig;
};

export const saveI18nConfig = (config: I18nConfig): void => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('i18n_config', JSON.stringify(config));
    }
  } catch (error) {
    console.error('Error saving i18n config:', error);
  }
};
